import {
  faFileContract,
  faHandHoldingUsd,
  faHouseUser,
  faUniversity,
  faPennant,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { BodyText, Card, Currency, Grid, Header3Text, NavigationItem, SpaceWrapper, Tooltip } from 'ui-toolkit';
import { IEscrowExpectedIncomingCheckData } from '../../types/accounting/data/IEscrowExpectedIncomingCheckData';
import { IEscrowExpectedIncomingWireData } from '../../types/accounting/data/IEscrowExpectedIncomingWireData';
import { showBorrowerNames } from '../../utilities/accounting/showBorrowerNames';
import { IEscrowActualIncomingWireData } from '../../types/accounting/data/IEscrowActualIncomingWireData';
import { IEscrowActualIncomingCheckData } from '../../types/accounting/data/IEscrowActualIncomingCheckData';
import { IEscrowExpectedIncomingAchData } from '../../types/accounting/data/IEscrowExpectedIncomingAchData';
import { IEscrowActualIncomingAchData } from '../../types/accounting/data/IEscrowActualIncomingAchData';
import { ClientUrlFactory } from '../../ui-toolkit/rest/ClientUrlFactory';
import { propertyAddressToString } from '../../ui-toolkit/orders/interfaces/IPropertyAddress';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';

const StyledIcon = styled(FontAwesomeIcon)`
  &&& {
    height: 16px;
    width: 16px;
  }
`;

const AreaSpacing = styled(Grid)`
  margin-top: ${(props) => props.theme.spacing.spacing4};
  margin-bottom: ${(props) => props.theme.spacing.spacing4};
  gap: ${(props) => props.theme.spacing.spacing2};
  grid-template-columns: min-content auto;
  align-items: center;
`;
const PrimaryColorText = css`
  color: ${(props) => props.theme.colors.primary};
`;
const BodyTextPrimaryColor = styled(BodyText)`
  ${PrimaryColorText}
`;
const StyledHeader3Text = styled(Header3Text)`
  margin-bottom: ${(props) => props.theme.spacing.spacing4};
`;

export interface IExpectedDetailCardProps {
  item: IEscrowExpectedIncomingWireData | IEscrowExpectedIncomingCheckData | IEscrowExpectedIncomingAchData;
  selectedIncomingActual: IEscrowActualIncomingWireData | IEscrowActualIncomingCheckData | IEscrowActualIncomingAchData;
  header?: ReactNode;
  footer?: ReactNode;
}

export const ExpectedDetailCard = ({ item, selectedIncomingActual, header, footer }: IExpectedDetailCardProps) => {
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const displayAddress = () => {
    const { streetAddress, city, stateAbbreviation, unit, zipCode } = item.escrowFile.order.propertyAddress;
    return propertyAddressToString({
      street_address: streetAddress,
      city,
      state: stateAbbreviation,
      unit: unit || null,
      zip_code: zipCode,
    });
  };

  const bank = item.escrowFile.depositEscrowBankAccount;
  const base = isSpruceWithZillowEnabled ? bank.legalName : bank.spruceName;
  const selectedNickname = isZillowClosingEnabled ? bank.nickname : bank.spruceNickname;
  const bankDisplayName = selectedNickname ? `${base} ${selectedNickname}` : base;

  return (
    <SpaceWrapper leftSpace={'none'} rightSpace={'none'}>
      <Card>
        <StyledHeader3Text>{header}</StyledHeader3Text>
        <Grid columnCount={2}>
          <BodyTextPrimaryColor>
            <Currency value={item.amountCents} valueIsCents={true} />
          </BodyTextPrimaryColor>
          <div>{DateTime.fromISO(item.createdAt).toFormat('D')}</div>
        </Grid>
        <AreaSpacing>
          <StyledIcon icon={faHouseUser} />
          <BodyText>{displayAddress()}</BodyText>
          <StyledIcon icon={faUniversity} />
          <BodyText>
            {bankDisplayName}
            {item.escrowFile.depositEscrowBankAccount.accountNumber !== selectedIncomingActual.accountNumber && (
              <Tooltip placement={'bottom'} tooltipContent={<BodyText>Bank account does not match</BodyText>}>
                <SpaceWrapper topSpace="none" bottomSpace="none">
                  <FontAwesomeIcon icon={faPennant} color={'red'} />
                </SpaceWrapper>
              </Tooltip>
            )}
          </BodyText>
          <StyledIcon icon={faFileContract} />{' '}
          <NavigationItem
            target={'_blank'}
            text={item.escrowFile.order.fileNumber.toString()}
            href={ClientUrlFactory().urlFor.orderStatus(item.escrowFile.order.id)}
            isInline
          />
          {!!item.escrowFile.order.parties.length && (
            <>
              <StyledIcon icon={faHandHoldingUsd} />
              <BodyText>{showBorrowerNames(item.escrowFile.order)}</BodyText>
            </>
          )}
        </AreaSpacing>
        {footer}
      </Card>
    </SpaceWrapper>
  );
};
